import { useState, useEffect } from "react";
import { templeApi } from "../api/templeApi";

export const useEvents = () => {
  const [rawEvents, setRawEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const generateSlug = (title) => {
    if (!title) return "";

    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  const parseSafeDate = (dateString) => {
    if (!dateString) return null;

    try {
      const value = String(dateString).trim();
      
      // Handle ISO format with timezone (2026-04-01T00:00:00.000000Z)
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})T/);
      if (isoMatch) {
        const [, year, month, day] = isoMatch;
        // Create date at noon UTC to avoid timezone issues
        return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0));
      }

      // YYYY-MM-DD format
      const simpleIsoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (simpleIsoMatch) {
        const [, year, month, day] = simpleIsoMatch;
        return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0));
      }

      // MM/DD/YYYY or DD/MM/YYYY
      const slashMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (slashMatch) {
        let [, first, second, year] = slashMatch;
        const month = Number(first);
        const day = Number(second);
        return new Date(Date.UTC(Number(year), month - 1, day, 12, 0, 0));
      }

      const date = new Date(value);
      if (isNaN(date.getTime())) return null;
      
      return date;
    } catch (error) {
      console.error("Error parsing date:", error);
      return null;
    }
  };

  const formatEventDate = (dateString) => {
    if (!dateString) return "Date TBA";

    try {
      const date = parseSafeDate(dateString);
      if (!date) return "Date TBA";

      return date.toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Date TBA";
    }
  };

  const formatEventTime = (timeString) => {
    if (!timeString) return "Time TBA";

    try {
      const value = String(timeString).trim();

      // Case 1: already 12-hour format like "08:00 AM"
      const twelveHourMatch = value.match(
        /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i
      );

      if (twelveHourMatch) {
        let [, hours, minutes, , period] = twelveHourMatch;
        return `${Number(hours)}:${minutes} ${period.toUpperCase()}`;
      }

      // Case 2: time part from ISO or plain 24-hour time like "08:00:00"
      const timeOnly = value.includes("T") ? value.split("T")[1] : value;

      const twentyFourHourMatch = timeOnly.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);

      if (twentyFourHourMatch) {
        let [, hours, minutes] = twentyFourHourMatch;
        const h = Number(hours);

        if (isNaN(h)) return "Time TBA";

        const period = h >= 12 ? "PM" : "AM";
        const displayHours = h % 12 || 12;

        return `${displayHours}:${minutes} ${period}`;
      }

      return "Time TBA";
    } catch (error) {
      console.error("Error formatting time:", error);
      return "Time TBA";
    }
  };

  const sortEventsByDate = (events) => {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    return [...events].sort((a, b) => {
      const dateA = a.event_venue?.start_date
        ? parseSafeDate(a.event_venue.start_date)
        : null;
      
      const dateB = b.event_venue?.start_date
        ? parseSafeDate(b.event_venue.start_date)
        : null;

      // Handle invalid dates
      const isValidDateA = dateA && !isNaN(dateA.getTime());
      const isValidDateB = dateB && !isNaN(dateB.getTime());

      if (!isValidDateA && !isValidDateB) return 0;
      if (!isValidDateA) return 1;
      if (!isValidDateB) return -1;

      // Reset time portion for fair comparison
      dateA.setUTCHours(0, 0, 0, 0);
      dateB.setUTCHours(0, 0, 0, 0);
      today.setUTCHours(0, 0, 0, 0);

      const isAPast = dateA < today;
      const isBPast = dateB < today;

      // Both future: sort by nearest first
      if (!isAPast && !isBPast) {
        return dateA - dateB;
      }

      // Both past: sort by most recent first
      if (isAPast && isBPast) {
        return dateB - dateA;
      }

      // One past, one future: future comes first
      if (!isAPast && isBPast) return -1;
      if (isAPast && !isBPast) return 1;

      return 0;
    });
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await templeApi.getEvents();

      if (response?.data?.success) {
        console.log("✅ Events Response:", response.data);

        const articles = response.data.data?.articles || [];
        setRawEvents(articles);

        console.log(
          "📅 Event Date/Time Summary:",
          articles.map((article) => ({
            title: article.article_title,
            start_date: article.event_venue?.start_date,
            start_time: article.event_venue?.start_time,
            end_date: article.event_venue?.end_date,
            end_time: article.event_venue?.end_time,
          }))
        );
      } else {
        throw new Error("Failed to fetch events data");
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      setError(err.message || "Something went wrong while fetching events");
      setRawEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const getSimpleEvents = () => {
    const sortedEvents = sortEventsByDate(rawEvents);

    return sortedEvents.map((article) => ({
      id: article.aid,
      slug: generateSlug(article.article_title),
      title: article.article_title,
      date: formatEventDate(article.event_venue?.start_date),
      image: article.assets?.[0]?.asset_value || "",
    }));
  };

  const getDetailedEvents = () => {
    const sortedEvents = sortEventsByDate(rawEvents);

    return sortedEvents.map((article) => ({
      id: article.aid,
      slug: generateSlug(article.article_title),
      title: article.article_title,
      subtitle: article.article_shortdesc || "",
      date: formatEventDate(article.event_venue?.start_date),
      image: article.assets?.[0]?.asset_value || "",
      venue:
        article.event_venue?.venue || "Sydney Sri Ayyappa Swami Centre, NSW",

      time: formatEventTime(article.event_venue?.start_time),

      startDate: article.event_venue?.start_date || null,
      startTime: article.event_venue?.start_time || null,
      endDate: article.event_venue?.end_date || null,
      endTime: article.event_venue?.end_time || null,

      formattedStartDate: formatEventDate(article.event_venue?.start_date),
      formattedStartTime: formatEventTime(article.event_venue?.start_time),
      formattedEndDate: formatEventDate(article.event_venue?.end_date),
      formattedEndTime: formatEventTime(article.event_venue?.end_time),

      mainHeading: article.article_title,
      description1: article.article_longdesc || "",
      description: article.article_shortdesc || "",
      breadcrumb: {
        home: "Home",
        current: article.article_title,
      },
      pageTitle: article.article_title,
    }));
  };

  const getEventById = (eventId) => {
    const article = rawEvents.find((event) => event.aid.toString() === eventId);

    if (!article) return null;

    return {
      id: article.aid,
      slug: generateSlug(article.article_title),
      title: article.article_title,
      subtitle: article.article_shortdesc || "",
      date: formatEventDate(article.event_venue?.start_date),
      image: article.assets?.[0]?.asset_value || "",
      venue:
        article.event_venue?.venue || "Sydney Sri Ayyappa Swami Centre, NSW",

      time: formatEventTime(article.event_venue?.start_time),

      startDate: article.event_venue?.start_date || null,
      startTime: article.event_venue?.start_time || null,
      endDate: article.event_venue?.end_date || null,
      endTime: article.event_venue?.end_time || null,

      formattedStartDate: formatEventDate(article.event_venue?.start_date),
      formattedStartTime: formatEventTime(article.event_venue?.start_time),
      formattedEndDate: formatEventDate(article.event_venue?.end_date),
      formattedEndTime: formatEventTime(article.event_venue?.end_time),

      mainHeading: article.article_title,
      description1: article.article_longdesc || "",
      description: article.article_shortdesc || "",
      breadcrumb: {
        home: "Home",
        current: article.article_title,
      },
      pageTitle: article.article_title,
    };
  };

  const getEventBySlug = (slug) => {
    return getDetailedEvents().find((event) => event.slug === slug);
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  return {
    rawEvents,
    loading,
    error,
    refetch: fetchEvents,
    getSimpleEvents,
    getDetailedEvents,
    getEventById,
    getEventBySlug,
    events: getSimpleEvents(),
  };
};